import { getAllActiveCoupons } from "./couponService";

// Función para validar si un cupón es aplicable según los criterios
export const validateCouponApplicability = async (coupon, invoiceData, selectedClient) => {
    try {
        // Si el cupón no tiene detalles, no es válido
        if (!coupon.Coupon_Details || coupon.Coupon_Details.length === 0) {
            return {
                isValid: false,
                reason: "El cupón no tiene detalles de aplicación"
            };
        }

        // Verificar que el cupón esté activo
        if (!coupon.Active) {
            return {
                isValid: false,
                reason: "El cupón no está activo"
            };
        }

        // Verificar fechas de validez
        const today = new Date();
        const startDate = coupon.Starting_Date ? new Date(coupon.Starting_Date) : null;
        const endDate = coupon.Ending_Date ? new Date(coupon.Ending_Date) : null;

        if (startDate && today < startDate) {
            return {
                isValid: false,
                reason: "El cupón aún no es válido"
            };
        }

        if (endDate && today > endDate) {
            return {
                isValid: false,
                reason: "El cupón ha expirado"
            };
        }

        // Verificar que la cantidad de descuento sea menor que el total descuento
        if (coupon.Discount_Amount && coupon.Total_Discounted_Amount) {
            if (coupon.Discount_Amount >= coupon.Total_Discounted_Amount) {
                return {
                    isValid: false,
                    reason: "El cupón ha alcanzado su límite de descuento"
                };
            }
        }

        // Verificar aplicabilidad según los detalles del cupón
        let hasValidDetail = false;
        const validationResults = [];

        for (const detail of coupon.Coupon_Details) {
            const detailValidation = await validateCouponDetail(detail, invoiceData, selectedClient);
            validationResults.push(detailValidation);

            if (detailValidation.isValid) {
                hasValidDetail = true;
            }
        }

        if (!hasValidDetail) {
            return {
                isValid: false,
                reason: "El cupón no es aplicable para este TPV, cliente o colegio",
                detailValidations: validationResults
            };
        }

        return {
            isValid: true,
            reason: "Cupón válido y aplicable",
            detailValidations: validationResults
        };

    } catch (error) {
        console.error("Error al validar cupón:", error);
        return {
            isValid: false,
            reason: "Error al validar el cupón"
        };
    }
};

// Función para validar un detalle específico del cupón
export const validateCouponDetail = async (detail, invoiceData, selectedClient) => {
    try {
        const currentTpvNo =
            typeof invoiceData?.tpv === "string"
                ? invoiceData.tpv
                : invoiceData?.tpv?.No || null;

        switch (detail.Detail_Type) {
            case "TPV":
                if (!currentTpvNo || currentTpvNo !== detail.No) {
                    return {
                        isValid: false,
                        reason: `Este cupón solo es válido para el TPV ${detail.No}, actual: ${currentTpvNo || 'desconocido'}`
                    };
                }
                break;

            case "Cliente":
                // Verificar cliente específico
                if (!selectedClient || selectedClient.No !== detail.No) {
                    return {
                        isValid: false,
                        reason: `Este cupón solo es válido para el cliente ${detail.No}`
                    };
                }
                break;

            case "Colegio":
                // Verificar colegio específico
                if (!invoiceData.Colegio || invoiceData.Colegio !== detail.No) {
                    return {
                        isValid: false,
                        reason: `Este cupón solo es válido para el colegio ${detail.No}`
                    };
                }
                break;

            case "Grupo precio cliente":
                // Verificar grupo de precios del cliente
                if (!selectedClient || selectedClient.Customer_Price_Group !== detail.No) {
                    return {
                        isValid: false,
                        reason: `Este cupón solo es válido para el grupo de precios ${detail.No}`
                    };
                }
                break;

            default:
                return {
                    isValid: false,
                    reason: `Tipo de detalle no reconocido: ${detail.Detail_Type}`
                };
        }

        return {
            isValid: true,
            reason: `Válido para ${detail.Detail_Type}: ${detail.No}`
        };

    } catch (error) {
        console.error("Error al validar detalle del cupón:", error);
        return {
            isValid: false,
            reason: "Error al validar detalle del cupón"
        };
    }
};

// Función para obtener cupones válidos para una factura específica
export const getValidCouponsForInvoice = async (invoiceData, selectedClient) => {
    try {
        // Obtener todos los cupones activos
        const allCoupons = await getAllActiveCoupons();

        // Filtrar cupones válidos
        const validCoupons = [];
        const invalidCoupons = [];

        for (const coupon of allCoupons) {
            const validation = await validateCouponApplicability(coupon, invoiceData, selectedClient);

            if (validation.isValid) {
                validCoupons.push({
                    ...coupon,
                    validation
                });
            } else {
                invalidCoupons.push({
                    ...coupon,
                    validation
                });
            }
        }

        return {
            validCoupons,
            invalidCoupons
        };

    } catch (error) {
        console.error("Error al obtener cupones válidos:", error);
        return {
            validCoupons: [],
            invalidCoupons: []
        };
    }
};

// Función para calcular el descuento aplicable de un cupón
export const calculateCouponDiscount = (coupon, invoiceTotal) => {
    try {
        let discountAmount = 0;

        // Si el cupón tiene un porcentaje de descuento
        if (coupon.Discount_Percentage && coupon.Discount_Percentage > 0) {
            discountAmount = invoiceTotal * (coupon.Discount_Percentage / 100);
        }

        // Si el cupón tiene una cantidad fija de descuento
        if (coupon.Discount_Amount && coupon.Discount_Amount > 0) {
            discountAmount = Math.min(coupon.Discount_Amount, invoiceTotal);
        }

        // Verificar que no exceda el límite total del cupón
        if (coupon.Total_Discounted_Amount && coupon.Total_Discounted_Amount > 0) {
            const remainingDiscount = coupon.Total_Discounted_Amount - (coupon.Discount_Amount || 0);
            discountAmount = Math.min(discountAmount, remainingDiscount);
        }

        return Math.max(0, discountAmount);

    } catch (error) {
        console.error("Error al calcular descuento del cupón:", error);
        return 0;
    }
};

// Función para obtener detalles de cupones válidos como opciones seleccionables
export const getValidCouponDetailsForInvoice = async (invoiceData, selectedClient, tpv) => {
    try {
        // Obtener todos los cupones activos
        const allCoupons = await getAllActiveCoupons();

        // Array para almacenar todos los detalles válidos
        const validCouponDetails = [];
        const invalidCouponDetails = [];

        for (const coupon of allCoupons) {
            // Verificar validez básica del cupón (fechas, estado, etc.)
            // Pasar el TPV obtenido de la caja en lugar del campo caja directamente
            const invoiceDataWithTPV = {
                ...invoiceData,
                tpv: typeof tpv === "string" ? tpv : tpv?.No || null // Guardar siempre el No del TPV
            };

            const basicValidation = await validateCouponApplicability(coupon, invoiceDataWithTPV, selectedClient);

            // Si el cupón tiene detalles, procesarlos individualmente
            if (coupon.Coupon_Details && coupon.Coupon_Details.length > 0) {
                for (const detail of coupon.Coupon_Details) {
                    // Validar cada detalle específicamente
                    const detailValidation = await validateCouponDetail(detail, invoiceDataWithTPV, selectedClient);

                    // Crear objeto de detalle con información completa
                    const couponDetailOption = {
                        // Información del cupón
                        couponNo: coupon.No,
                        couponDescription: coupon.Description,
                        discountPercentage: coupon.Discount_Percentage || 0,
                        discountAmount: coupon.Discount_Amount || 0,
                        totalDiscountedAmount: coupon.Total_Discounted_Amount || 0,

                        // Información del detalle
                        detailType: detail.Detail_Type,
                        detailNo: detail.No,
                        detailDiscountPercentage: detail.Discount_Percentage || 0,
                        detailDiscountAmount: detail.Discount_Amount || 0,
                        detailTotalDiscountedAmount: detail.Total_Discounted_Amount || 0,
                        detailStatus: detail.Status,

                        // Validación
                        isValid: basicValidation.isValid && detailValidation.isValid,
                        validation: {
                            couponValidation: basicValidation,
                            detailValidation: detailValidation
                        },

                        // Clave única para el selector
                        key: `${coupon.No}-${detail.Detail_Type}-${detail.No}`,

                        // Label para mostrar en el selector
                        label: `${coupon.No} - ${coupon.Description} (${detail.Detail_Type}: ${detail.No})`,

                        // Información completa del cupón y detalle originales
                        originalCoupon: coupon,
                        originalDetail: detail
                    };

                    if (couponDetailOption.isValid) {
                        validCouponDetails.push(couponDetailOption);
                    } else {
                        invalidCouponDetails.push(couponDetailOption);
                    }
                }
            }
        }

        return {
            validCouponDetails,
            invalidCouponDetails,
            allCouponDetails: [...validCouponDetails, ...invalidCouponDetails]
        };

    } catch (error) {
        console.error("Error al obtener detalles de cupones válidos:", error);
        return {
            validCouponDetails: [],
            invalidCouponDetails: [],
            allCouponDetails: []
        };
    }
}; 